import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import SV from 'server-vars';
import { getPublisherTagInstance } from '../utils/getPublisherTagInstance';
import { loadGpt } from '../utils/gptLoader';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { type SlotRenderEndedEvent } from '../types';

type Props = {
    adId: string;
    elementId: string;
    width: number;
    height: number;
    className?: string;
    hasResponsiveHeightWidth?: boolean;
    onSlotRenderEnded?: (event: SlotRenderEndedEvent, element: HTMLDivElement) => void;
    lazyLoad?: boolean;
};

export const AdUnit: FC<Props> = ({
    adId,
    elementId,
    width,
    height,
    className,
    hasResponsiveHeightWidth = false,
    onSlotRenderEnded,
    lazyLoad = false,
}) => {
    const adRef = useRef<HTMLDivElement>(null);

    const [gptLoaded, setGptLoaded] = useState(false);
    const [adSlot, setAdSlot] = useState<googletag.Slot | null>(null);

    const triggerGptLoad = useCallback(async (): Promise<void> => {
        await loadGpt();
        setGptLoaded(true);
    }, []);

    useEffect(() => {
        return () => {
            if (adSlot) {
                const gt = getPublisherTagInstance();
                gt?.destroySlots([adSlot]);
            }
        };
    }, [adSlot]);

    useEffect(() => {
        if (!lazyLoad && SV.get('isSbAdsEnabled')) {
            triggerGptLoad();
        }
    }, [lazyLoad, triggerGptLoad]);

    useEffect(() => {
        if (gptLoaded && !adSlot) {
            const gt = getPublisherTagInstance();

            if (hasResponsiveHeightWidth) {
                gt?.pubads().addEventListener('slotRenderEnded', event => {
                    if (adRef.current) {
                        onSlotRenderEnded?.(event, adRef.current);
                    }
                });
            }
            gt?.cmd.push(() => {
                gt?.pubads();

                // define ad slot
                const slot = gt.defineSlot(adId, [width, height], elementId);
                if (slot) {
                    // configure ad slot
                    slot.addService(gt.pubads());
                }

                if (!gt.pubadsReady) {
                    gt.pubads().enableSingleRequest();
                    gt.enableServices();
                }

                setAdSlot(slot);
            });

            gt?.cmd.push(() => {
                gt?.display(elementId);
            });
        }
    }, [
        gptLoaded,
        adSlot,
        hasResponsiveHeightWidth,
        onSlotRenderEnded,
        adId,
        width,
        height,
        elementId,
    ]);

    return (
        <>
            {!!lazyLoad && (
                <VisibilityTracker
                    onVisibilityChange={({ isVisible }) => {
                        if (isVisible && SV.get('isSbAdsEnabled')) {
                            triggerGptLoad();
                        }
                    }}
                    elementRef={adRef}
                    observerOptions={{
                        rootMargin: `200% 0px`, // Render slots within 2 viewports.
                    }}
                />
            )}
            <div
                style={{ minHeight: height }}
                className={className}
                id={elementId}
                data-tn="ad-unit"
                data-target-width={width}
                data-target-height={height}
                ref={adRef}
            />
        </>
    );
};
